// Service API pour communiquer avec le backend
// import { getApiUrl } from '../utils/apiUrls';

// En production : même origine via NPM (/api → backend) — évite les erreurs CORS
// En développement : proxy /api via setupProxy.js
const API_BASE_URL = '/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.clearLegacyTokens();
  }

  clearLegacyTokens() {
    try {
      sessionStorage.removeItem('adminToken');
      localStorage.removeItem('adminToken');
    } catch (storageError) {
      console.warn('Impossible de nettoyer les anciens tokens:', storageError);
    }
  }

  getUploadRequestInit() {
    return { credentials: 'include' };
  }

  // Méthode pour faire des requêtes HTTP
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const bodyPreview = await response.text();
        if (bodyPreview.trimStart().startsWith('<!DOCTYPE') || bodyPreview.trimStart().startsWith('<html')) {
          if (response.status === 502) {
            throw new Error(
              'API indisponible (502) — vérifiez que taxi-abel-backend tourne (port 3001) et que NPM route /api vers le frontend ou le backend.'
            );
          }
          if (process.env.NODE_ENV === 'development') {
            throw new Error(
              'Réponse HTML au lieu de JSON — lancez le backend local (port 3010) et npm run dev (proxy /api).'
            );
          }
          throw new Error(
            'Réponse HTML au lieu de JSON — l\'API /api ne répond pas correctement. Redéployez le frontend (proxy API) ou configurez NPM : /api → taxi-abel-backend:3001.'
          );
        }
        throw new Error(`Réponse inattendue (${response.status}): ${bodyPreview.slice(0, 120)}`);
      }
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          const authError = data.error || '';
          if (authError === 'Token expiré' || authError === 'Token invalide' || authError === 'Token d\'accès requis') {
            this.clearLegacyTokens();
            throw new Error(
              'Session admin expirée — reconnectez-vous sur /admin'
            );
          }
        }
        throw new Error(data.error || 'Erreur de requête');
      }

      return data;
    } catch (error) {
      console.error('Erreur API:', error);
      throw error;
    }
  }

  // =====================================================
  // AUTHENTIFICATION
  // =====================================================

  async login(username, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    this.clearLegacyTokens();
    return response;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      this.clearLegacyTokens();
    }
  }

  async verifyToken() {
    return await this.request('/auth/verify');
  }

  async getProfile() {
    return await this.request('/auth/profile');
  }

  // =====================================================
  // PARAMÈTRES
  // =====================================================

  async getSettings() {
    return await this.request('/settings');
  }

  async getSetting(key) {
    if (key === 'appearance') {
      return await this.request('/public/settings/appearance');
    }
    return await this.request(`/settings/${key}`);
  }

  async updateSetting(key, value, description) {
    return await this.request(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ setting_value: value, description }),
    });
  }

  async updateAppearancePreservingImages(value) {
    return await this.request('/settings/appearance/preserve-images', {
      method: 'PUT',
      body: JSON.stringify({ setting_value: value }),
    });
  }

  async getCompanyInfo() {
    const response = await this.request('/public/settings/company/info');
    return response;
  }

  async updateCompanyInfo(companyInfo) {
    return await this.request('/settings/company/info', {
      method: 'PUT',
      body: JSON.stringify(companyInfo),
    });
  }

  async getPricing() {
    return await this.request('/public/settings/pricing');
  }

  async updatePricing(pricingData) {
    return await this.request('/pricing', {
      method: 'PUT',
      body: JSON.stringify(pricingData),
    });
  }

  // =====================================================
  // LIEUX
  // =====================================================

  async getLocations(type = null, activeOnly = true) {
    // Utiliser la route publique pour les lieux
    return await this.request('/public/locations');
  }


  async getLocation(id) {
    return await this.request(`/locations/${id}`);
  }

  async createLocation(locationData) {
    return await this.request('/locations', {
      method: 'POST',
      body: JSON.stringify(locationData),
    });
  }

  async updateLocation(id, locationData) {
    return await this.request(`/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(locationData),
    });
  }

  async deleteLocation(id) {
    return await this.request(`/locations/${id}`, {
      method: 'DELETE',
    });
  }

  async updateDistance(distanceData) {
    return await this.request('/locations/distances', {
      method: 'POST',
      body: JSON.stringify(distanceData),
    });
  }

  async getDistances() {
    return await this.request('/locations/distances/all');
  }

  async getDistanceMatrix() {
    return await this.request('/locations/distances/matrix');
  }

  async deleteDistance(fromId, toId) {
    return await this.request(`/locations/distances/${fromId}/${toId}`, {
      method: 'DELETE',
    });
  }

  async calculatePrice(fromLocationId, toLocationId, departureTime, passengers = 1) {
    return await this.request('/locations/calculate-price', {
      method: 'POST',
      body: JSON.stringify({
        from_location_id: fromLocationId,
        to_location_id: toLocationId,
        departure_time: departureTime,
        passengers,
      }),
    });
  }

  // =====================================================
  // RÉSERVATIONS
  // =====================================================

  async getReservations(page = 1, limit = 20, status = null, search = null) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    
    return await this.request(`/reservations?${params.toString()}`);
  }

  async getReservation(id) {
    return await this.request(`/reservations/${id}`);
  }

  async createReservation(reservationData) {
    return await this.request('/reservations', {
      method: 'POST',
      body: JSON.stringify(reservationData),
    });
  }

  async updateReservation(id, reservationData) {
    return await this.request(`/reservations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reservationData),
    });
  }

  async deleteReservation(id) {
    return await this.request(`/reservations/${id}`, {
      method: 'DELETE',
    });
  }

  async getReservationStats(period = '30') {
    return await this.request(`/reservations/stats/overview?period=${period}`);
  }

  async getReservationsByStatus(status, page = 1, limit = 20) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    return await this.request(`/reservations/status/${status}?${params.toString()}`);
  }

  // =====================================================
  // TARIFS
  // =====================================================

  async getPricingRules(activeOnly = true, ruleType = null) {
    const params = new URLSearchParams();
    if (activeOnly !== null) params.append('active_only', activeOnly.toString());
    if (ruleType) params.append('rule_type', ruleType);
    
    const queryString = params.toString();
    return await this.request(`/pricing${queryString ? `?${queryString}` : ''}`);
  }

  async getPricingRule(id) {
    return await this.request(`/pricing/${id}`);
  }

  async createPricingRule(ruleData) {
    return await this.request('/pricing', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    });
  }

  async updatePricingRule(id, ruleData) {
    return await this.request(`/pricing/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ruleData),
    });
  }

  async deletePricingRule(id) {
    return await this.request(`/pricing/${id}`, {
      method: 'DELETE',
    });
  }

  async getActivePricing() {
    return await this.request('/pricing/current/active');
  }

  async getPricingByType(ruleType) {
    return await this.request(`/pricing/type/${ruleType}`);
  }

  async togglePricingRule(id) {
    return await this.request(`/pricing/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  async getPricingHistory(ruleType, limit = 50) {
    return await this.request(`/pricing/history/${ruleType}?limit=${limit}`);
  }

  async calculateDetailedPrice(fromLocationId, toLocationId, departureTime, passengers = 1) {
    return await this.request('/pricing/calculate', {
      method: 'POST',
      body: JSON.stringify({
        from_location_id: fromLocationId,
        to_location_id: toLocationId,
        departure_time: departureTime,
        passengers,
      }),
    });
  }

  // =====================================================
  // ACTUALITÉS
  // =====================================================

  async getNews() {
    return await this.request('/public/news');
  }

  async getNewsAdmin() {
    return await this.request('/news/admin');
  }

  async getNewsItem(id) {
    return await this.request(`/news/${id}`);
  }

  async createNews(newsData) {
    return await this.request('/news', {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
  }

  async updateNews(id, newsData) {
    return await this.request(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsData),
    });
  }

  async deleteNews(id) {
    return await this.request(`/news/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderNews(newsItems) {
    return await this.request('/news/reorder', {
      method: 'PUT',
      body: JSON.stringify({ newsItems }),
    });
  }

  // =====================================================
  // SERVICES
  // =====================================================

  async getServices() {
    return await this.request('/public/services');
  }

  async getServicesAdmin() {
    return await this.request('/services/admin');
  }

  async getService(id) {
    return await this.request(`/services/${id}`);
  }

  async createService(serviceData) {
    return await this.request('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateService(id, serviceData) {
    return await this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  async deleteService(id) {
    return await this.request(`/services/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderServices(services) {
    return await this.request('/services/reorder', {
      method: 'PUT',
      body: JSON.stringify({ services }),
    });
  }

  // =====================================================
  // FLUX RSS
  // =====================================================

  async getRSSFeeds() {
    return await this.request('/rss/feeds');
  }

  async createRSSFeed(feedData) {
    return await this.request('/rss/feeds', {
      method: 'POST',
      body: JSON.stringify(feedData),
    });
  }

  async updateRSSFeed(feedId, feedData) {
    return await this.request(`/rss/feeds/${feedId}`, {
      method: 'PUT',
      body: JSON.stringify(feedData),
    });
  }

  async deleteRSSFeed(feedId) {
    return await this.request(`/rss/feeds/${feedId}`, {
      method: 'DELETE',
    });
  }

  async testRSSFeed(url) {
    return await this.request(`/rss/test/${encodeURIComponent(url)}`);
  }

  async importRSSFeed(feedId) {
    return await this.request(`/rss/import/${feedId}`, {
      method: 'POST',
    });
  }

  async importAllRSSFeeds() {
    return await this.request('/rss/import-all', {
      method: 'POST',
    });
  }

  // =====================================================
  // AVIS GOOGLE
  // =====================================================

  async getReviews() {
    return await this.request('/reviews');
  }

  async getReviewsAdmin() {
    return await this.request('/reviews/admin');
  }

  async createReview(reviewData) {
    return await this.request('/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async updateReview(reviewId, reviewData) {
    return await this.request(`/reviews/${reviewId}`, {
      method: 'PUT',
      body: JSON.stringify(reviewData),
    });
  }

  async deleteReview(reviewId) {
    return await this.request(`/reviews/${reviewId}`, {
      method: 'DELETE',
    });
  }

  async toggleReview(reviewId) {
    return await this.request(`/reviews/${reviewId}/toggle`, {
      method: 'PATCH',
    });
  }

  async reorderReviews(reviews) {
    return await this.request('/reviews/reorder', {
      method: 'PUT',
      body: JSON.stringify({ reviews }),
    });
  }


  // =====================================================
  // WIDGET GOOGLE NATIF (100% GRATUIT)
  // =====================================================

  async getGoogleWidgetConfig() {
    return await this.request('/google-widget');
  }

  async updateGoogleWidgetConfig(config) {
    return await this.request('/google-widget', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  async testGoogleWidgetConfiguration() {
    return await this.request('/google-widget/test', {
      method: 'POST',
    });
  }

  async getGoogleWidgetCode() {
    return await this.request('/google-widget/widget-code');
  }

  async getGoogleWidgetUrls() {
    return await this.request('/google-widget/urls');
  }

  // =====================================================
  // AVIS GOOGLE RÉELS
  // =====================================================

  async getGoogleReviews() {
    return await this.request('/google-reviews');
  }

  async getGoogleReviewsStats() {
    return await this.request('/google-reviews/stats');
  }

  // =====================================================
  // LIENS SOCIAUX
  // =====================================================

  async getSocialLinks() {
    return await this.request('/social-links/public');
  }

  async getSocialLinksAdmin() {
    return await this.request('/social-links');
  }

  async createSocialLink(data) {
    return await this.request('/social-links', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSocialLink(id, data) {
    return await this.request(`/social-links/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSocialLink(id) {
    return await this.request(`/social-links/${id}`, {
      method: 'DELETE',
    });
  }

  // =====================================================
  // UTILITAIRES
  // =====================================================

  async healthCheck() {
    return await this.request('/health');
  }

  // =====================================================
  // SEO
  // =====================================================

  async getSEOSettings() {
    return await this.request('/seo/settings');
  }

  async updateSEOSettings(seoSettings) {
    return await this.request('/seo/settings', {
      method: 'PUT',
      body: JSON.stringify(seoSettings),
    });
  }

  async regenerateHomepage() {
    return await this.request('/seo/regenerate', {
      method: 'POST',
    });
  }

  // =====================================================
  // AGENT SEO GEMINI
  // =====================================================

  async getSeoAgentConfig() {
    return await this.request('/seo-agent/config');
  }

  async updateSeoAgentConfig(config) {
    const response = await this.request('/seo-agent/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
    return response;
  }

  async generateSeoArticle(options = {}) {
    return await this.request('/seo-agent/generate', {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async getSeoAgentArticles() {
    return await this.request('/seo-agent/articles');
  }

  async deleteSeoAgentArticle(articleId) {
    return await this.request(`/seo-agent/articles/${articleId}`, {
      method: 'DELETE',
    });
  }

  async getPublicSeoArticle(slug) {
    return await this.request(`/seo-agent/articles/public/${slug}`);
  }

  async getPublicBlogArticles() {
    return await this.request('/seo-agent/articles/public');
  }

  async getSeoAgentSocialConfig() {
    return await this.request('/seo-agent/social-config');
  }

  async updateSeoAgentSocialConfig(config) {
    return await this.request('/seo-agent/social-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async publishSeoArticleToSocial(articleId) {
    return await this.request(`/seo-agent/articles/${articleId}/publish-social`, {
      method: 'POST',
    });
  }
}

// Créer une instance unique du service
const apiService = new ApiService();

export default apiService;
